import { Component, OnInit } from '@angular/core';
import { filter, firstValueFrom, map, pairwise, startWith, Subject, takeUntil } from 'rxjs';
import {
  EBackendFilterTypes,
  IGenericCrudConfiguration
} from 'src/app/libs/components/generic-crud/generic-crud.component';
import { Batch, ConsignmentStock, ProductBatchState } from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';
import { IContextualizableResource, IListableResource } from 'src/app/resources';
import { ProductsBatchEditComponent } from './products-batch-edit/products-batch-edit.component';
import { ProductsBatchAddComponent } from './products-batch-add/products-batch-add.component';
import { ProductsEditComponent } from '../products-operative-products/products-edit/products-edit.component';
import { StorageService } from 'src/app/services/storage.service';
import { ProductPricesComponent } from '../products-operative-products/components/product-prices/product-prices.component';
import { ProductStockComponent } from '../products-operative-products/components/product-stock/product-stock.component';
import { ProductSubscribersComponent } from '../products-operative-products/components/product-subscribers/product-subscribers.component';
import { AclService } from 'src/app/libs/services/acl.service';
import {
  ProductProcesSheetsComponent
} from '../products-operative-products/product-proces-sheets/product-proces-sheets.component';
import {
  ProductSimulationsComponent
} from '../products-operative-products/product-simulations/product-simulations.component';

@Component({
    selector: 'app-products-batch',
    templateUrl: './products-batch.component.html',
    styleUrl: './products-batch.component.sass',
    standalone: false
})
export class ProductsBatchComponent implements OnInit {

  private destroy$ = new Subject<void>();
  config?: IGenericCrudConfiguration<Batch>;
  consignmentStocks: ConsignmentStock[] = [];
  isAdmin = this.acl.isAdmin()
  productBatchStates: ProductBatchState[] = [];

  constructor(private api: MasterApiService, private acl: AclService, private storage: StorageService) {
        this.destroy$ = new Subject<void>();
  }

  async ngOnInit(): Promise<void> {
        this.api.auth.user$.pipe(
          takeUntil(this.destroy$),
          filter(u => u !== null && u !== undefined),
          map(u =>  u!.lastCompanyId),
          startWith(null),
          pairwise(),
          filter(([prev, curr]) => prev !== curr),
          map(([, curr]) => curr)
        ).subscribe(async () => {
    const companyId = this.api.auth.userValue?.lastCompanyId;
    const companies = this.api.auth.companiesValue;
    const currencies = await firstValueFrom(this.storage.getCurrencies())
    const currencyId = companies.find(x => x.id === companyId)?.currencyId;
    const currency = currencies.find(c => c.id === currencyId);
    const precision = currency?.decimals;
    const currencyFormat = { type: 'currency', precision: precision };
    this.productBatchStates = await firstValueFrom(this.api.resources.product.productBatchStates.getAll({ companyId: companyId }));
    this.consignmentStocks = await firstValueFrom(this.api.resources.product.consignmentStocks.getAll());
    const batchResource = this.api.resources.product.batch as unknown as (IListableResource<Batch> & IContextualizableResource);

    this.config = {
      name: this.api.resources.product.batch.resourceName,
      resource: batchResource,
      editComponent: ProductsBatchEditComponent,
      createComponent: ProductsBatchAddComponent,
      entityFields: {
        batch: {  isLink: true, dataType: 'shortstring' },
        productCode: {
          dataType: 'code',
          editor: {
            component: ProductsEditComponent,
            inputPrms: async (row) => {
              const innerEditors: unknown[] = []
                  innerEditors.push({
                    name: 'price',
                    component: ProductPricesComponent,
                    inputPrms: { id: row.productId }
                  })
                  innerEditors.push({
                    name: 'stock',
                    component: ProductStockComponent,
                    inputPrms: { id: row.productId }
                  })
                  innerEditors.push({
                    name:'processsheets',
                    component: ProductProcesSheetsComponent,
                    inputPrms: { productId: row }
                  })
                  innerEditors.push({
                    name:'simulations',
                    component: ProductSimulationsComponent,
                    inputPrms: { id: row }
                  })
                  if (this.isAdmin) {
                    innerEditors.push({
                      name: 'subscribers',
                      component: ProductSubscribersComponent,
                      inputPrms: { id: row.productId },
                      isAdminConfig: true
                    });
                  }
                  return {
                    id: row.productId,
                    innerEditors: innerEditors,
                  };
             }
          },
          isLink: true
        },
        productName: {dataType: 'longstring',
          editor: {
            component: ProductsEditComponent,
            inputPrms: async (row) => {
             const innerEditors: unknown[] = []
                 innerEditors.push({
                   name: 'price',
                   component: ProductPricesComponent,
                   inputPrms: { id: row.productId }
                 })
                 innerEditors.push({
                   name: 'stock',
                   component: ProductStockComponent,
                   inputPrms: { id: row.productId }
                 })
                 if (this.isAdmin) {
                   innerEditors.push({
                     name: 'subscribers',
                     component: ProductSubscribersComponent,
                     inputPrms: { id: row.productId },
                     isAdminConfig: true
                   });
                 }
                 return {
                   id: row.productId,
                   innerEditors: innerEditors,
                 };
            }
          },
          isLink: true
        },
        registerDate: {dataType: 'date'},
        productBatchStateId: {
          dataType: 'code',
          caption: 'entity.batch.productBatchStateId',
          cellTemplate: 'productBatchStateColorTpl',
          lookup: {
            dataSource: this.productBatchStates,
            displayExpr: 'name',
            valueExpr: 'id',
          }
        },
        'stock.stock': {  isLink: true, dataType: 'number', caption: 'entity.batch.stock' },
        'stock.available': {  isLink: true, dataType: 'number', caption: 'entity.batch.available' },
        providerBatch: { dataType: 'code' },
        expirationDate: {dataType: 'date'},
        standardCost: {
          dataType: 'number',
          format: currencyFormat,
        },
        batchCost: {
          dataType: 'number',
          format: currencyFormat,
        },
        standardCostVariance:{
          caption: 'entity.purchaseOrderProducts.costVariance',
          cellTemplate: 'costVarianceTpl',
          dataType: 'number',
        },
        consignmentStockId: {
          dataType: 'shortstring',
          lookup:{
            dataSource: this.consignmentStocks,
            displayExpr: 'description',
            valueExpr: 'id'
          },
          visible: false,
        },
        'stock.batchSize': {  isLink: true, dataType: 'code', caption: 'entity.batch.batchSize', visible: false },
      },
      backendFilters:[
        {
          type: EBackendFilterTypes.Text,
          queryParam: 'Batch',
          config: {
            label: 'entity.batch.batch',
          }
        },
        {
          type: EBackendFilterTypes.Text,
          queryParam: 'ProviderBatch',
          config: {
            label: 'entity.batch.providerBatch',
          }
        },
        {
          type: EBackendFilterTypes.Lookup,
          queryParam: 'productId',
          config: {
            label: 'common.tabTitle.products',
            gsResource: this.api.resources.product.products,
            displayExpr: 'name',
            valueExpr: 'id',
            itemTemplateValues: {
              code: 'code',
              reference: 'eanCode',
              name: 'name',
              boldCode: true,
            },
            searchExpr: ['code', 'name', 'eanCode'],
          }
        },
        {
          type: EBackendFilterTypes.StatesTagBox,
          queryParam: 'productBatchStateIds',
          config: {
            label: 'entity.purchaseOrder.states',
            items: this.productBatchStates,
            displayExpr: 'name',
            valueExpr: 'id',
          }
        },
        ]
    }
  })
}
}
